import * as React from 'react';
import { useEffect, useState } from 'react';
import { AxisLabelsFormatterContextObject, Point } from 'highcharts';

import type { GetAssetResponse } from '@blockworks/platform/api/research';
import { getUtcOffset } from '@blockworks/platform/libs';
import { TradingViewChart } from '@blockworks/ui';

import CloseIcon from '@/assets/icons/close-icon';
import SecondaryButton from '@/components/display/buttons/secondary-button';
import ChartCheckboxes from '@/components/pages/price/components/chart-checkboxes';
import ChartTabs from '@/components/pages/price/components/chart-tabs';
import TradingViewIcon from '@/components/pages/price/components/trading-view-ico';

import { usePriceChart } from '../hooks/use-price-chart';

import { PriceChart } from './price-chart';

export interface IChartCheckboxes {
    name: string;
    current: boolean;
}

export interface IChartTabs {
    name: string;
    current: boolean;
}

type PricePageChartProps = {
    asset: GetAssetResponse['data'];
    setOpenPrice: (p: number) => void;
    setOpenPriceBtc: (p: number) => void;
    setPrice: (p: number) => void;
    setIsBtc: (b: boolean) => void;
};

const BTC_COLOR = 'orange';
const USD_COLOR = '#5637CD';
const dateToUnix = (date: any): number => {
    if (date instanceof Date) {
        return Math.floor(date.getTime() / 1000);
    }
    const toDateType = new Date(date);

    // round it to nearest minute
    return Math.floor(toDateType.getTime() / 1000000) * 1000;
};

const getFirstDayOfYearUnix = (): number => {
    const currentYear = new Date().getFullYear();
    const firstDayOfYear = new Date(currentYear, 0, 1);
    return dateToUnix(firstDayOfYear);
};

const getOneYearAgoUnix = (): number => {
    const currentDate = new Date();
    const oneYearAgo = currentDate.setFullYear(currentDate.getFullYear() - 1);
    return dateToUnix(oneYearAgo);
};

const getThreeMonthsAgoUnix = (): number => {
    const currentDate = new Date();
    const threeMonthsAgo = currentDate.setMonth(currentDate.getMonth() - 3);
    return dateToUnix(threeMonthsAgo);
};

const getOneMonthAgoUnix = (): number => {
    const currentDate = new Date();
    const oneMonthAgo = currentDate.setMonth(currentDate.getMonth() - 1);
    return dateToUnix(oneMonthAgo);
};

const getOneWeekAgoUnix = (): number => {
    const currentDate = new Date();
    const oneWeekAgo = currentDate.setDate(currentDate.getDate() - 7);
    return dateToUnix(oneWeekAgo);
};

const getOneDayAgoUnix = (): number => {
    const currentDate = new Date();
    const oneDayAgo = currentDate.setDate(currentDate.getDate() - 1);
    return dateToUnix(oneDayAgo);
};

const getStartingTimestampUnix = (timeframe: string): number => {
    if (timeframe === 'YTD') {
        return getFirstDayOfYearUnix();
    }
    if (timeframe === '1Y') {
        return getOneYearAgoUnix();
    }
    if (timeframe === '3M') {
        return getThreeMonthsAgoUnix();
    }
    if (timeframe === '1M') {
        return getOneMonthAgoUnix();
    }
    if (timeframe === '7D') {
        return getOneWeekAgoUnix();
    }
    if (timeframe === '1D') {
        return getOneDayAgoUnix();
    }
    return 1230958800;
};

const getDataForChartType = (priceChart: any, chartTypeTabs: IChartTabs[]) => {
    if (chartTypeTabs.find(chartTypeTab => chartTypeTab.current)?.name === 'Price') {
        return priceChart?.prices;
    }
    if (chartTypeTabs.find(chartTypeTab => chartTypeTab.current)?.name === 'Market Cap') {
        return priceChart?.market_caps;
    }
    return [];
};

const PricePageChart = (props: PricePageChartProps) => {
    const { asset, setPrice, setIsBtc, setOpenPrice, setOpenPriceBtc } = props;
    const { cgRef } = asset;

    const [tradingViewOpened, setTradingViewOpened] = useState<boolean>(false);

    const [chartTypeTabs, setChartTypeTabs] = useState<IChartTabs[]>([
        { name: 'Price', current: true },
        { name: 'Market Cap', current: false },
    ]);

    const [chartZoomTabs, setChartZoomTabs] = useState<IChartTabs[]>([
        { name: '1D', current: true },
        { name: '7D', current: false },
        { name: '1M', current: false },
        { name: '3M', current: false },
        { name: '1Y', current: false },
        { name: 'YTD', current: false },
        { name: 'ALL', current: false },
    ]);

    const [chartScaleTabs, setChartScaleTabs] = useState<IChartTabs[]>([
        { name: 'Linear', current: false },
        { name: 'Logarithmic', current: true },
    ]);

    const [chartVsCurrencyCheckboxes, setChartVsCurrencyCheckboxes] = useState<IChartCheckboxes[]>([
        { name: 'USD', current: true },
        { name: 'BTC', current: false },
    ]);

    const priceChartUsd = usePriceChart(
        cgRef || '',
        'usd',
        getStartingTimestampUnix(chartZoomTabs.find(chartZoomTab => chartZoomTab.current)?.name || ''),
        Math.floor(Date.now() / 1000) + 3600,
    );

    const priceChartBtc = usePriceChart(
        cgRef || '',
        'btc',
        getStartingTimestampUnix(chartZoomTabs.find(chartZoomTab => chartZoomTab.current)?.name || ''),
        Math.floor(Date.now() / 1000) + 3600,
    );

    const onClickChartTypeTab = (e: any) => {
        setChartTypeTabs(
            chartTypeTabs.map(chartTypeTab => {
                return e.target.text === chartTypeTab.name
                    ? { ...chartTypeTab, current: true }
                    : { ...chartTypeTab, current: false };
            }),
        );
    };

    const onClickChartZoomTab = (e: any) => {
        setChartZoomTabs(
            chartZoomTabs.map(chartZoomTab => {
                return e.target.text === chartZoomTab.name
                    ? { ...chartZoomTab, current: true }
                    : { ...chartZoomTab, current: false };
            }),
        );
    };

    const onClickChartScaleTab = (e: any) => {
        setChartScaleTabs(
            chartScaleTabs.map(chartScaleTab => {
                return e.target.text === chartScaleTab.name
                    ? { ...chartScaleTab, current: true }
                    : { ...chartScaleTab, current: false };
            }),
        );
    };

    const onChangeChartVsCurrencyCheckboxes = (e: any) => {
        setChartVsCurrencyCheckboxes(
            chartVsCurrencyCheckboxes.map(chartVsCurrencyCheckbox => {
                if (e.target.id === chartVsCurrencyCheckbox.name) {
                    return {
                        ...chartVsCurrencyCheckbox,
                        current: e.target.checked,
                    };
                }
                return chartVsCurrencyCheckbox;
            }),
        );

        const usdChecked = chartVsCurrencyCheckboxes.find(b => b.name === 'USD')?.current ?? false;
        setIsBtc(
            (e.target.id === 'BTC' && e.target.checked && !usdChecked) || (e.target.id === 'USD' && !e.target.checked),
        );
    };

    const onChartLineHover = (point: Point) => {
        if (point?.y) {
            setPrice && setPrice(point.y);
            setIsBtc && setIsBtc(point.color === BTC_COLOR);
        }
    };

    const onChartLineMouseOut = () => {};

    // Set usd and btc open price (for dynamic change calculation)
    useEffect(() => {
        if (!priceChartUsd) {
            return;
        }
        const prices = priceChartUsd.prices && priceChartUsd.prices.length > 0 ? priceChartUsd.prices[0] : null;
        if (prices && prices.length > 0) {
            setOpenPrice(prices[1]!);
        }
    }, [priceChartUsd]);

    useEffect(() => {
        if (!priceChartBtc) {
            return;
        }
        const prices = priceChartBtc.prices && priceChartBtc.prices.length > 0 ? priceChartBtc.prices[0] : null;
        if (prices && prices.length > 0) {
            setOpenPriceBtc(prices[1]!);
        }
    }, [priceChartBtc]);

    if (tradingViewOpened) {
        return (
            <div className="relative pb-10 z-10 w-full h-[500px] flex flex-col">
                <div className="absolute z-10 right-0 bottom-3 md:bottom-1">
                    <SecondaryButton size="small" onClick={() => setTradingViewOpened(false)}>
                        <CloseIcon style="black" className="-ml-0.5 md:mr-1 w-3" />{' '}
                        <span className="hidden md:flex">Close</span>
                    </SecondaryButton>
                </div>
                {asset.code && (
                    <TradingViewChart
                        interval={'all'}
                        symbol={asset.tradingViewRef ?? `BINANCE:${asset.code.toUpperCase()}USD`}
                        themeName="light"
                        className="h-full w-full self-stretch grow"
                        autoSize
                    />
                )}
            </div>
        );
    }

    return (
        <div>
            {asset && (
                <div>
                    <div className="mt-1 flex flex-col sm:flex-row gap-5 justify-between">
                        <ChartTabs tabs={chartTypeTabs} onClick={onClickChartTypeTab} />
                        <ChartTabs tabs={chartZoomTabs} onClick={onClickChartZoomTab} />
                    </div>
                    <div className="flex flex-col-reverse sm:flex-row gap-2 justify-between py-4">
                        {asset.code !== 'BTC' ? (
                            <div className="w-full flex justify-center sm:justify-start">
                                <ChartCheckboxes
                                    checkboxes={chartVsCurrencyCheckboxes}
                                    onChange={onChangeChartVsCurrencyCheckboxes}
                                />
                            </div>
                        ) : (
                            <div />
                        )}
                        <div className="w-full flex justify-center md:justify-end">
                            <SecondaryButton onClick={() => setTradingViewOpened(true)}>
                                <TradingViewIcon className="-ml-0.5 mt-0.5 md:mr-1 w-6" /> TradingView Chart
                            </SecondaryButton>
                        </div>
                        <ChartTabs tabs={chartScaleTabs} onClick={onClickChartScaleTab} />
                    </div>
                    <div>
                        <PriceChart
                            allowChartUpdate={true}
                            options={{
                                chart: {
                                    height: 400,
                                },
                                credits: {
                                    enabled: false,
                                },
                                rangeSelector: {
                                    enabled: false,
                                    selected: 5,
                                },
                                yAxis: [
                                    {
                                        title: {
                                            text: undefined,
                                        },
                                        height: '85%',
                                        resize: {
                                            enabled: true,
                                        },
                                        opposite: false,
                                        labels: {
                                            align: 'left',
                                            x: -10,
                                            formatter: (obj: AxisLabelsFormatterContextObject) => String(obj.value),
                                            style: {
                                                color: '#000000',
                                                fontSize: '11px',
                                            },
                                        },
                                        type:
                                            chartScaleTabs
                                                .find(chartScaleTab => chartScaleTab.current)
                                                ?.name.toLowerCase() === 'linear'
                                                ? 'linear'
                                                : 'logarithmic',
                                    },
                                    {
                                        title: {
                                            text: undefined,
                                        },
                                        height: '85%',
                                        resize: {
                                            enabled: true,
                                        },
                                        opposite: true,
                                        labels: {
                                            align: 'right',
                                            x: -10,
                                            style: {
                                                color: '#000000',
                                                fontSize: '11px',
                                            },
                                        },
                                        type:
                                            chartScaleTabs
                                                .find(chartScaleTab => chartScaleTab.current)
                                                ?.name.toLowerCase() === 'linear'
                                                ? 'linear'
                                                : 'logarithmic',
                                        gridLineWidth: 0,
                                    },
                                    {
                                        title: {
                                            text: undefined,
                                        },
                                        top: '75%',
                                        height: '25%',
                                        labels: {
                                            enabled: false,
                                        },
                                        visible: false,
                                    },
                                ],
                                xAxis: {
                                    title: {
                                        text: undefined,
                                    },

                                    type: 'datetime',
                                    crosshair: true,
                                    minRange: 1,
                                },
                                tooltip: {
                                    enabled: false,
                                },
                                series: [
                                    {
                                        name: chartTypeTabs.find(chartTypeTab => chartTypeTab.current)?.name || '',
                                        type: 'area',
                                        yAxis: 0,
                                        showInNavigator: true,
                                        data: chartVsCurrencyCheckboxes.find(
                                            chartVsCurrencyCheckbox => chartVsCurrencyCheckbox.name === 'USD',
                                        )?.current
                                            ? getDataForChartType(priceChartUsd, chartTypeTabs)
                                            : [],
                                        color: USD_COLOR,
                                        marker: {
                                            enabled: false,
                                        },
                                    },
                                    {
                                        name: chartTypeTabs.find(chartTypeTab => chartTypeTab.current)?.name || '',
                                        type: 'area',
                                        yAxis: 1,
                                        showInNavigator: false,
                                        data: chartVsCurrencyCheckboxes.find(
                                            chartVsCurrencyCheckbox => chartVsCurrencyCheckbox.name === 'BTC',
                                        )?.current
                                            ? getDataForChartType(priceChartBtc, chartTypeTabs)
                                            : [],
                                        color: BTC_COLOR,
                                        marker: {
                                            enabled: false,
                                            states: {
                                                hover: {
                                                    enabled: true,
                                                    radius: 5,
                                                },
                                            },
                                        },
                                    },
                                    {
                                        name: 'Volume',
                                        type: 'column',
                                        data: priceChartUsd?.total_volumes || [],
                                        yAxis: 2,
                                        color: USD_COLOR,
                                        opacity: 0.5,
                                    },
                                ],
                                plotOptions: {
                                    area: {
                                        fillColor: {
                                            linearGradient: {
                                                x1: 0,
                                                y1: 0,
                                                x2: 0,
                                                y2: 1,
                                            },
                                            stops: [
                                                [0, 'rgba(86,55,205,0.75)'],
                                                [1, 'rgba(255,255,255,0.5)'],
                                            ],
                                        },
                                        point: {
                                            events: {
                                                mouseOver(this: Point) {
                                                    onChartLineHover(this);
                                                },
                                                mouseOut: () => {
                                                    onChartLineMouseOut();
                                                },
                                            },
                                        },
                                    },
                                    series: {
                                        lineWidth: 1.2,
                                        states: {
                                            hover: {
                                                lineWidth: 1.7,
                                            },
                                        },
                                    },
                                },
                                exporting: {
                                    enabled: false,
                                },
                                navigator: {
                                    enabled: true,
                                    height: 17.5,
                                    series: {
                                        lineWidth: 0.25,
                                    },
                                    adaptToUpdatedData: false,
                                    maskInside: true,
                                    maskFill: 'rgba(86,55,205,0.2)',
                                    opposite: false,
                                    outlineColor: '#ffffff',
                                    outlineWidth: 0,
                                    handles: {
                                        backgroundColor: '#ffffff',
                                        borderColor: '#5637CD',
                                        enabled: true,
                                        height: 17.5,
                                        lineWidth: 0.75,
                                        symbols: ['navigator-handle', 'navigator-handle'],
                                        width: 6,
                                    },
                                },
                                scrollbar: {
                                    enabled: false,
                                },
                                time: {
                                    getTimezoneOffset: getUtcOffset,
                                },
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default PricePageChart;
